import React, { useState, useEffect } from 'react';
import { Menu, Bell, User, LogOut, Settings, ChevronDown, Mail, Phone, Shield, Activity, Calendar, FileText, CheckCircle, Building2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { NotificationDropdown } from '../UI/NotificationDropdown';
import { Link, useNavigate } from 'react-router-dom';
import { userManagementService } from '../../services/userManagementService';
import OrganizationSelector from '../Organization/OrganizationSelector';
import { useNotificationCounts } from '../../hooks/useNotifications';
import UserAvatar from '../UI/UserAvatar';

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const {
    user,
    organization,
    organizations,
    logout,
    switchOrganization,
    canSwitchOrganizations,
    availableWorkspaces,
    workspaceMode,
    setWorkspaceMode,
  } = useAuth();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
  const [userStats, setUserStats] = useState({
    reports: 0,
    filings: 0,
    compliance: 0,
    tasks: 0
  });

  const { data: notificationCounts } = useNotificationCounts();
  const unreadCount = notificationCounts?.total ?? 0;
  const accountTypeLabel = user?.account_type === 'ca' ? 'CA' : 'Business';
  const workspaceLabel = workspaceMode === 'ca' ? 'CA' : 'Business';
  const accountTypeBadgeClassName =
    user?.account_type === 'ca'
      ? 'bg-purple-100 text-purple-800'
      : 'bg-slate-100 text-slate-800';

  // Load user statistics and notification count
  useEffect(() => {
    const loadUserStats = async () => {
      if (user) {
        try {
          // Set initial stats to zero for new users - real data will come from API calls
          setUserStats({
            reports: 0,
            filings: 0,
            compliance: 0,
            tasks: 0
          });
          // TODO: Replace with real API calls when user stats endpoints are available
          // const stats = await userStatsService.getUserStats();
          // setUserStats(stats);
        } catch (_error) {
          // console.error('Error loading user stats:', error);
        }
      }
    };

    loadUserStats();
  }, [user]);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
    } catch (_error) {
      toast.error('Logout failed');
    }
  };

  const handleProfileClick = () => {
    setUserMenuOpen(false);
    navigate('/settings');
  };

  const handleActivityClick = () => {
    setUserMenuOpen(false);
    navigate('/audit-logs');
  };

  const handleOrganizationChange = async (organizationId: string) => {
    try {
      await switchOrganization(organizationId);
      // No need to reload - the context will handle the state update
      // Data will be refreshed automatically through React context
    } catch (_error) {
      toast.error('Failed to switch organization');
    }
  };

  const handleWorkspaceChange = (mode: 'business' | 'ca') => {
    setWorkspaceMode(mode);
    if (mode === 'ca' && user?.needs_ca_workspace_setup === true) {
      navigate('/ca/workspace-setup');
      return;
    }
    navigate(mode === 'ca' ? '/ca/dashboard' : '/dashboard');
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="flex h-14 items-center justify-between px-4 sm:h-16 sm:px-5 lg:h-[4.5rem]">
        {/* Left side - Menu button and title */}
        <div className="flex items-center min-w-0 flex-1">
          <button
            type="button"
            className="lg:hidden p-1.5 sm:p-2 lg:p-3 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-colors"
            onClick={onMenuClick}
          >
            <span className="sr-only">Open sidebar</span>
            <Menu className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
          </button>
          
          <div className="ml-2 sm:ml-4 lg:ml-6 xl:ml-8 min-w-0 flex-1 flex items-center">
            <Link
              to={workspaceMode === 'ca' ? '/ca/dashboard' : '/dashboard'}
              className="flex min-w-0 shrink-0 items-center rounded-md hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
              aria-label={workspaceMode === 'ca' ? 'Go to CA dashboard' : 'Go to dashboard'}
            >
              <img
                src="/cmndf-logo.png"
                alt=""
                className="h-10 sm:h-11 md:h-12 w-auto max-w-[min(100%,11rem)] sm:max-w-[13rem] md:max-w-[15rem] object-contain object-left pointer-events-none"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </Link>
          </div>
        </div>

        {/* Right side - Organization Selector, Notifications and user menu */}
        <div className="flex items-center gap-responsive-1">
          {/* Organization Selector (for admins with multiple orgs) */}
          {availableWorkspaces.length > 1 && (
            <select
              value={workspaceMode}
              onChange={(event) => handleWorkspaceChange(event.target.value as 'business' | 'ca')}
              className="hidden md:block border border-gray-200 rounded-lg px-2.5 py-2 text-sm bg-white"
            >
              <option value="business">Business Workspace</option>
              <option value="ca">CA Workspace</option>
            </select>
          )}

          {/* Organization Selector (for admins with multiple orgs) */}
          {canSwitchOrganizations() && organizations.length > 1 && (
            <div className="max-w-xs w-full">
              <OrganizationSelector
                currentOrganizationId={organization?.id || ''}
                onOrganizationChange={handleOrganizationChange}
                className="shadow-sm"
              />
            </div>
          )}
          {/* Show organization name badge if only one org or switcher hidden */}
          {(!canSwitchOrganizations() || organizations.length <= 1) && organization && (
            <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
              <Building2 className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 truncate max-w-[9.375rem]">
                {organization.name}
              </span>
            </div>
          )}
          {/* Notifications */}
          <div className="relative">
            <button
              type="button"
              className="relative p-1.5 sm:p-2 lg:p-3 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-colors"
              onClick={() => setNotificationMenuOpen(!notificationMenuOpen)}
            >
              <span className="sr-only">View notifications</span>
              <Bell className="h-5 w-5 sm:h-6 sm:w-6 lg:h-7 lg:w-7" />
              {unreadCount > 0 && (
                <>
                  <span className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 lg:top-1.5 lg:right-1.5 h-1.5 w-1.5 sm:h-2 sm:w-2 lg:h-2.5 lg:w-2.5 bg-red-500 rounded-full"></span>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center h-5 w-5 text-xs font-bold text-white bg-red-500 rounded-full">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </>
              )}
            </button>
            
            {/* Notification Dropdown */}
            <NotificationDropdown 
              isOpen={notificationMenuOpen} 
              onClose={() => setNotificationMenuOpen(false)} 
            />
          </div>

          {/* User menu */}
          <div className="relative">
            <button
              type="button"
              className="flex items-center gap-responsive-1 p-1.5 sm:p-2 lg:p-3 rounded-lg text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 transition-all"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <span className="sr-only">Open user menu</span>
              <UserAvatar
                name={user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
                size="sm"
                className="lg:h-12 lg:w-12 lg:text-base ring-0"
              />
              <div className="hidden lg:block text-left">
                <p className="text-responsive-xs font-medium text-gray-900">
                  {user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User Name'}
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  {user?.role && (
                    <p className="text-responsive-xs text-blue-600 font-medium">
                      {userManagementService.getRoleDisplayName(user.role)}
                    </p>
                  )}
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5">{workspaceLabel}</p>
              </div>
              <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 lg:h-5 lg:w-5 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* User dropdown menu */}
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-72 sm:w-80 lg:w-96 xl:w-[28rem] bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50 max-h-[calc(100vh-8rem)] overflow-y-auto">
                {/* User Profile Header */}
                <div className="px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center space-x-3">
                    <UserAvatar
                      name={user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
                      size="lg"
                      className="ring-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User Name'}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {user?.email || 'user@example.com'}
                      </p>
                      <div className="flex items-center mt-1 space-x-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${accountTypeBadgeClassName}`}>
                          <Building2 className="w-3 h-3 mr-1" />
                          {`Acct: ${accountTypeLabel}`}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            workspaceMode === 'ca' ? 'bg-indigo-100 text-indigo-800' : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          <Activity className="w-3 h-3 mr-1" />
                          {`WS: ${workspaceLabel}`}
                        </span>
                      </div>
                      <div className="flex items-center mt-1 space-x-2">
                        {user?.role && (
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${userManagementService.getRoleColor(user.role)}`}>
                            <Shield className="w-3 h-3 mr-1" />
                            {userManagementService.getRoleDisplayName(user.role)}
                          </span>
                        )}
                        {user?.is_active && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Active
                          </span>
                        )}
                      </div>
                      {organization && (
                        <p className="text-xs text-gray-400 mt-1 truncate">
                          {organization.name}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quick Stats */}
                <div className="px-6 py-3 border-b border-gray-100">
                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div className="flex flex-col items-center">
                      <FileText className="h-4 w-4 text-blue-500 mb-1" />
                      <p className="text-lg font-semibold text-gray-900">{userStats.reports}</p>
                      <p className="text-xs text-gray-500">Reports</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <Calendar className="h-4 w-4 text-green-500 mb-1" />
                      <p className="text-lg font-semibold text-gray-900">{userStats.filings}</p>
                      <p className="text-xs text-gray-500">Filings</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <CheckCircle className="h-4 w-4 text-purple-500 mb-1" />
                      <p className="text-lg font-semibold text-gray-900">{userStats.compliance}%</p>
                      <p className="text-xs text-gray-500">Compliance</p>
                    </div>
                    <div className="flex flex-col items-center">
                      <Activity className="h-4 w-4 text-orange-500 mb-1" />
                      <p className="text-lg font-semibold text-gray-900">{userStats.tasks}</p>
                      <p className="text-xs text-gray-500">Tasks</p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  {/* Organization Switcher in Mobile Menu - Only show if multiple orgs */}
                  {canSwitchOrganizations() && organizations.length > 1 && (
                    <div className="px-6 py-3 border-b border-gray-100 sm:border-none">
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Switch Organization</p>
                        <OrganizationSelector
                          currentOrganizationId={organization?.id || ''}
                          onOrganizationChange={handleOrganizationChange}
                          className="w-full"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Create Organization - Only for admins */}
                  {canSwitchOrganizations() && (
                    <Link
                      to="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center px-6 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Building2 className="h-4 w-4 mr-3 text-gray-400" />
                      <div>
                        <p className="font-medium">
                          {organizations.length > 1 ? 'Manage Organizations' : 'Create Organization'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {organizations.length > 1 
                            ? `You have ${organizations.length} organizations` 
                            : 'Add a new organization'}
                        </p>
                      </div>
                    </Link>
                  )}

                  <button
                    onClick={handleProfileClick}
                    className="flex items-center w-full px-6 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                  >
                    <Settings className="h-4 w-4 mr-3 text-gray-400" />
                    <div>
                      <p className="font-medium">Settings</p>
                      <p className="text-xs text-gray-500">Manage your preferences</p>
                    </div>
                  </button>
                  
                  <Link
                    to="/settings"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center px-6 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User className="h-4 w-4 mr-3 text-gray-400" />
                    <div>
                      <p className="font-medium">Profile</p>
                      <p className="text-xs text-gray-500">View your profile</p>
                    </div>
                  </Link>
                  
                  <button
                    onClick={handleActivityClick}
                    className="flex items-center w-full px-6 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                  >
                    <Activity className="h-4 w-4 mr-3 text-gray-400" />
                    <div>
                      <p className="font-medium">Activity</p>
                      <p className="text-xs text-gray-500">Recent activity</p>
                    </div>
                  </button>
                </div>

                {/* Contact Info */}
                <div className="px-6 py-3 border-t border-gray-100 bg-gray-50">
                  <div className="space-y-2">
                    <div className="flex items-center text-xs text-gray-600">
                      <Mail className="h-3 w-3 mr-2" />
                      <span className="truncate">{user?.email || 'user@example.com'}</span>
                    </div>
                    {user?.phone && (
                      <div className="flex items-center text-xs text-gray-600">
                        <Phone className="h-3 w-3 mr-2" />
                        <span>{user.phone}</span>
                      </div>
                    )}
                    {user?.last_login && (
                      <div className="flex items-center text-xs text-gray-500">
                        <Calendar className="h-3 w-3 mr-2" />
                        <span>Last login: {new Date(user.last_login).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Logout */}
                <div className="px-6 py-3 border-t border-gray-100">
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <LogOut className="h-4 w-4 mr-3" />
                    <span className="font-medium">Sign out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Click outside to close user menu */}
      {userMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setUserMenuOpen(false)}
        />
      )}

      {/* Click outside to close notification menu */}
      {notificationMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setNotificationMenuOpen(false)}
        />
      )}
    </header>
  );
};

export default Header;
